// The two store badges — "Download on the App Store" and "GET IT ON Google
// Play" — drawn the way the stores' own badges look: black, a thin grey
// keyline, the mark on the left, a small line over a big one.
//
// One component for every place the app is advertised on the web (the
// download popup and the bottom-centre slot on /umanitoba), so the two badges
// cannot drift into looking like two different products. The URLs come from
// utils/appStores.js for the same reason.
//
// Drawn in markup rather than shipped as the stores' artwork files: they stay
// sharp at any size, need no extra request, and the text is real text for a
// screen reader. Order is the caller's (`detectOs` puts the visitor's own
// store first); both are always there, because a laptop opens these pages too.

import { APP_STORE_URL, PLAY_STORE_URL } from '../utils/appStores.js';

/**
 * @param {object} p
 * @param {'ios'|'android'|null} [p.first] which badge leads; Apple when null
 * @param {(store: 'ios'|'android') => void} [p.onTap] called on the way out
 * @param {string} [p.className] extra class on the row
 */
export default function StoreBadges({ first = null, onTap, className = '' }) {
  const apple = (
    <a key="ios" className="sb-badge" href={APP_STORE_URL} target="_blank" rel="noopener noreferrer"
       onClick={() => onTap?.('ios')} data-store="ios" aria-label="Download on the App Store">
      <AppleIcon />
      <span><small>Download on the</small><b>App Store</b></span>
    </a>
  );
  const play = (
    <a key="android" className="sb-badge" href={PLAY_STORE_URL} target="_blank" rel="noopener noreferrer"
       onClick={() => onTap?.('android')} data-store="android" aria-label="Get it on Google Play">
      <PlayIcon />
      <span><small className="sb-caps">Get it on</small><b>Google Play</b></span>
    </a>
  );
  return (
    <div className={`sb-row${className ? ` ${className}` : ''}`}>
      <style>{CSS}</style>
      {first === 'android' ? [play, apple] : [apple, play]}
    </div>
  );
}

function AppleIcon() {
  return (
    <svg className="sb-mark" width="24" height="28" viewBox="0 0 24 24" fill="#fff" aria-hidden="true">
      <path d="M16.4 12.7c0-2.3 1.9-3.4 2-3.5-1.1-1.6-2.8-1.8-3.4-1.8-1.4-.1-2.8.9-3.5.9s-1.8-.8-3-.8c-1.5 0-2.9.9-3.7 2.3-1.6 2.7-.4 6.8 1.1 9 .8 1.1 1.6 2.3 2.8 2.2 1.1 0 1.6-.7 2.9-.7s1.7.7 2.9.7 2-1.1 2.7-2.2c.9-1.2 1.2-2.4 1.2-2.5 0 0-2.4-.9-2.4-3.6zM14.2 5.9c.6-.8 1-1.8.9-2.9-.9 0-2 .6-2.6 1.4-.6.7-1.1 1.7-.9 2.8 1 0 2-.5 2.6-1.3z" />
    </svg>
  );
}

/** The Play triangle in its four colours. */
function PlayIcon() {
  return (
    <svg className="sb-mark" width="24" height="26" viewBox="0 0 24 26" aria-hidden="true">
      <path d="M1.2 1.1C.9 1.4.7 1.9.7 2.6v20.8c0 .7.2 1.2.5 1.5L12.9 13 1.2 1.1z" fill="#00a0ff" />
      <path d="M16.8 17 12.9 13l3.9-3.9 4.7 2.7c1.3.8 1.3 2 0 2.8L16.8 17z" fill="#ffd400" />
      <path d="M16.8 17 12.9 13 1.2 24.9c.4.5 1.2.5 2 .1L16.8 17" fill="#ff3a44" />
      <path d="M16.8 9.1 3.2 1.1C2.4.6 1.6.7 1.2 1.1L12.9 13l3.9-3.9z" fill="#32bc6c" />
    </svg>
  );
}

// Inline, like the popup and MobilePage: the CSP allows inline styles.
const CSS = `
.sb-row {
  display: flex; flex-wrap: wrap; justify-content: center; gap: 10px;
}
.sb-badge {
  flex: 0 0 auto;
  display: inline-flex; align-items: center; gap: 8px;
  height: 48px; min-width: 150px; padding: 0 14px 0 11px; box-sizing: border-box;
  background: #000; color: #fff; text-decoration: none;
  border: 1px solid #a6a6a6; border-radius: 9px;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
  -webkit-tap-highlight-color: transparent;
  transition: transform .1s ease, box-shadow .15s ease;
}
.sb-badge:hover { box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.18) inset; }
.sb-badge:active { transform: scale(0.97); }
.sb-badge:focus-visible { outline: 2px solid #1a73e8; outline-offset: 2px; }
.sb-mark { flex: none; display: block; }
.sb-badge span { display: flex; flex-direction: column; text-align: left; line-height: 1; white-space: nowrap; }
.sb-badge small { font-size: 10.5px; font-weight: 500; letter-spacing: 0.01em; margin-bottom: 2px; }
.sb-badge small.sb-caps { text-transform: uppercase; font-size: 9.5px; letter-spacing: 0.04em; }
.sb-badge b { font-size: 19px; font-weight: 600; letter-spacing: -0.015em; }
/* Two modifiers on the row, chosen by the caller:
   .sb-fill  — inside a card: the pair share the width equally, and when the
               card is too narrow for both they stack at the same width,
               rather than as two ragged pills.
   .sb-float — over the map: compact, so the pair sits clear of the map's
               own corner controls the way the single button before it did,
               and shadowed so black holds up over satellite imagery. */
.sb-fill .sb-badge { flex: 1 1 140px; min-width: 0; justify-content: center; }
.sb-float { gap: 8px; }
.sb-float .sb-badge {
  height: 40px; min-width: 0; padding: 0 12px 0 9px; gap: 7px; border-radius: 8px;
  box-shadow: 0 3px 14px rgba(0, 0, 0, 0.38);
}
.sb-float .sb-mark { width: 20px; height: 22px; }
.sb-float .sb-badge small { font-size: 9px; margin-bottom: 1px; }
.sb-float .sb-badge small.sb-caps { font-size: 8px; }
.sb-float .sb-badge b { font-size: 16px; }
@media (prefers-reduced-motion: reduce) { .sb-badge { transition: none; } }
`;
